// /docs: the guide — what Brainyard Share is, how to install it and use it
// (docs.ts). Readable signed in or not: someone deciding whether to install
// is who needs it first. Its contents list the sections under their groups,
// the one being read marked, folded above the text on a narrow screen
// (byj-1wv.51.22).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { commandLines, docGroups, docs, type DocBlock } from "../docs";
import { AuthorCommands } from "../components/CommandLine";
import { CopyButton } from "../components/Copy";
import { DocText } from "../components/DocText";
import { Link } from "../components/Nav";
import { Page, PageHead } from "../components/Page";
import { FoldPanel } from "../components/SidePanel";
import { useMedia } from "../media";
import { language, t } from "../i18n";

/** A heading with a link to itself to copy, beside it rather than in it, so the heading's name is its words alone. */
function Heading({ level, id, text }: { level: 2 | 3; id: string; text: string }) {
  const H = level === 2 ? "h2" : "h3";
  return (
    <div className="docs-heading">
      <H id={level === 3 ? id : undefined}>{text}</H>
      <CopyButton icon text={`${window.location.origin}/docs#${id}`} label={t("shell:docsCopyLink", { title: text })} />
    </div>
  );
}

function Block({ b }: { b: DocBlock }): ReactNode {
  switch (b.kind) {
    case "p":
      return (
        <p>
          <DocText s={b.text} />
        </p>
      );
    case "heading":
      return <Heading level={3} id={b.id} text={b.text} />;
    case "note":
      return (
        <p className="doc-note">
          <DocText s={b.text} />
        </p>
      );
    case "code":
      return <AuthorCommands commands={commandLines(b.text)} />;
    case "list":
      return (
        <ul>
          {b.items.map((it, i) => (
            <li key={i}>
              <DocText s={it} />
            </li>
          ))}
        </ul>
      );
    case "table": {
      // a table of more than two columns names each cell's column where a
      // narrow screen stacks a row's cells (styles.css .doc-table)
      const named = b.head.length > 2;
      return (
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                {b.head.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j} data-label={named && j > 0 ? b.head[j] : undefined}>
                      <DocText s={c} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }
}

/** What scrolls `el` up and down: the nearest ancestor that scrolls (the shell's pane), else the document. */
function scrollerOf(el: Element | null): Element {
  for (let e = el?.parentElement; e; e = e.parentElement) {
    const y = getComputedStyle(e).overflowY;
    if ((y === "auto" || y === "scroll") && e.scrollHeight > e.clientHeight) return e;
  }
  return document.scrollingElement ?? document.documentElement;
}

const nextFrame = (f: () => void) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(f) : setTimeout(f, 0));

/**
 * The section being read: the first whose text reaches into the top third
 * of the screen below the top bar. A link lands a section under the bar and
 * 1rem (styles.css `--topbar-h`, scroll-margin-top); the band starts 8px
 * below that, so the section landed on is not passed over for the end of the
 * one before it. A section picked — from the contents, or by the link the
 * page opened on — is the one being read until the page is scrolled up or
 * down from where the pick left it, by any means; then the band names it
 * again. The last sections cannot scroll to the top, so the band alone would
 * name another; and a table scrolled sideways, or the contents scrolled, is
 * not the page scrolled.
 */
function useReading(sectionIds: string[]): [string | undefined, (id: string) => void] {
  const [reading, setReading] = useState<string | undefined>(sectionIds[0]);
  // where the page rested once the pick's own scroll was done
  const held = useRef<{ at?: number } | null>(null);
  const scroller = useRef<Element | null>(null);
  const key = sectionIds.join(" ");
  useEffect(() => {
    const ids = key.split(" ");
    scroller.current = scrollerOf(document.getElementById(ids[0]!));
    if (typeof IntersectionObserver !== "function") return;
    const root = getComputedStyle(document.documentElement);
    const below = (parseFloat(root.getPropertyValue("--topbar-h")) || 48) + (parseFloat(root.fontSize) || 16) + 8;
    const inView = new Set<string>();
    const first = () => ids.find((id) => inView.has(id));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) inView.add(e.target.id);
          else inView.delete(e.target.id);
        }
        const f = first();
        if (f && !held.current) setReading(f);
      },
      { rootMargin: `-${Math.round(below)}px 0px -66% 0px` },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    }
    const scrolled = (e: Event) => {
      const h = held.current;
      const sc = scroller.current;
      const target = e.target === document ? document.scrollingElement : e.target;
      if (!h || h.at === undefined || !sc || target !== sc || Math.abs(sc.scrollTop - h.at) <= 2) return;
      held.current = null;
      const f = first();
      if (f) setReading(f);
    };
    const opts = { capture: true, passive: true };
    document.addEventListener("scroll", scrolled, opts);
    return () => {
      io.disconnect();
      document.removeEventListener("scroll", scrolled, opts);
    };
  }, [key]);
  const pick = useCallback((id: string) => {
    const h: { at?: number } = {};
    held.current = h;
    setReading(id);
    // the jump to it is scrolled before the next frame: where it rests is the mark's place
    nextFrame(() => {
      if (held.current === h) h.at = scroller.current?.scrollTop ?? 0;
    });
  }, []);
  return [reading, pick];
}

/** The groups and their sections, each linked; the one being read marked and, where the list scrolls, kept in its view. */
function Contents({ groups, reading, onPick }: { groups: ReturnType<typeof docGroups>; reading?: string; onPick: (section: string, target: string) => void }) {
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    const list = nav.current;
    const a = list?.querySelector('a[aria-current="location"]');
    if (!list || !a || list.scrollHeight <= list.clientHeight) return;
    // the list alone is scrolled, never the page: scrollIntoView would move both;
    // a group's first section shows with the group's name over it
    const group = a.closest(".docs-toc-group");
    const top = group?.querySelector("a") === a ? group : a;
    const [l, r, t] = [list.getBoundingClientRect(), a.getBoundingClientRect(), top.getBoundingClientRect()];
    if (t.top < l.top) list.scrollTop -= l.top - t.top + (top === a ? 8 : 0);
    else if (r.bottom > l.bottom) list.scrollTop += r.bottom - l.bottom + 8;
  }, [reading]);
  return (
    <nav ref={nav} className="docs-toc" aria-label={t("shell:docsContents")}>
      {groups.map((g) => (
        <div key={g.id} className="docs-toc-group">
          <p className="docs-toc-title">{g.title}</p>
          <ul>
            {g.sections.map((s) => {
              const subs = s.blocks.flatMap((b) => (b.kind === "heading" ? [b] : []));
              return (
                <li key={s.id}>
                  <a href={`#${s.id}`} aria-current={reading === s.id ? "location" : undefined} onClick={() => onPick(s.id, s.id)}>
                    {s.title}
                  </a>
                  {subs.length > 0 && (
                    <ul>
                      {subs.map((h) => (
                        <li key={h.id}>
                          <a href={`#${h.id}`} onClick={() => onPick(s.id, h.id)}>
                            {h.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function Docs({ signedIn }: { signedIn: boolean }) {
  const lang = language();
  const sections = useMemo(() => docs(), [lang]);
  const groups = useMemo(() => docGroups(), [lang]);
  const [reading, pick] = useReading(sections.map((s) => s.id));
  const narrow = useMedia("(max-width: 720px)", false);
  // folded above the text, the contents close on a pick, which then lands
  // where the text is once they are closed
  const [folded, setFolded] = useState(false);
  const picked = useCallback(
    (section: string, target: string) => {
      if (!narrow) return pick(section);
      setFolded(false);
      nextFrame(() => {
        document.getElementById(target)?.scrollIntoView?.();
        pick(section);
      });
    },
    [narrow, pick],
  );
  // a link to one section (/docs#templates) lands on it: the browser looked
  // for the section before it was drawn, and a link inside the app never
  // asks; and the contents mark it, as they mark one picked there. So does
  // a link followed on the page — the guide's own (#machines), one through
  // the app (/docs#files) or Back and Forward between them — each told by
  // popstate alone: the browser's own fires hashchange as well, and the
  // app's navigate fires nothing else. Only the app's is scrolled here: the
  // browser scrolls for its own, a link followed and Back and Forward alike,
  // and a second scroll on top of it is one too many. Sections are read as
  // they are now, in the language shown
  useEffect(() => {
    const land = (scroll: boolean) => {
      // a navigation away from the guide is told too, before the page goes
      if (window.location.pathname.replace(/\/+$/, "") !== "/docs") return;
      // a hash no section has, or one that is not a well-formed escape
      // (#%E0%A4), leaves the page where it is: on the top when it opens
      let id = "";
      try {
        id = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }
      if (!id) return;
      if (scroll) document.getElementById(id)?.scrollIntoView?.();
      const owner = docs().find((s) => s.id === id || s.blocks.some((b) => b.kind === "heading" && b.id === id));
      if (owner) pick(owner.id);
    };
    land(true);
    const told = (e: PopStateEvent) => land(!e.isTrusted);
    window.addEventListener("popstate", told);
    return () => window.removeEventListener("popstate", told);
  }, []);
  const contents = <Contents groups={groups} reading={reading} onPick={picked} />;
  // the text is drawn once a language: the mark moving as the page scrolls redraws the contents alone
  const article = useMemo(
    () => (
      <article className="docs-text">
        {groups.map((g) => (
          <Fragment key={g.id}>
            <p className="docs-group">{g.title}</p>
            {g.sections.map((s) => (
              <section key={s.id} id={s.id}>
                <Heading level={2} id={s.id} text={s.title} />
                {s.blocks.map((b, i) => (
                  <Block key={i} b={b} />
                ))}
              </section>
            ))}
          </Fragment>
        ))}
      </article>
    ),
    [groups],
  );
  return (
    <Page className="docs">
      <PageHead title={t("shell:docsTitle")} actions={!signedIn && <Link to="/login">{t("shell:signIn")}</Link>} />
      <div className="docs-body">
        {narrow ? (
          <FoldPanel
            label={t("shell:docsContents")}
            summary={<span className="muted">{sections.find((s) => s.id === reading)?.title}</span>}
            className="panel docs-toc-fold"
            open={folded}
            onToggle={setFolded}
          >
            {contents}
          </FoldPanel>
        ) : (
          contents
        )}
        {article}
      </div>
    </Page>
  );
}
